import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import md5 from 'md5';
import { FaTrashAlt, FaBars, FaWhatsapp } from 'react-icons/fa';
import { Menu, Item, useContextMenu } from 'react-contexify';
import {
  Form,
  Button,
  Card,
  Alert,
  Modal,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from 'react-bootstrap';
import useEcg from '../../../hooks/useEcg';
import api from '../../../services/api';
import {
  requestLaudoPresignDownload,
  uploadBlobToLaudo,
} from '../../../services/laudoFiles';
import { ecg as dbecg } from '../../../helpers/laudos/ecg';
import styles from '../../../styles.module.css';
import history from '../../../services/history';
import RichTextEditor from '../../../components/RichTextEditor';
import { selectProntuarioById } from '../../../redux/features/prontuario/prontuarioSlice';
import CustomFileInput from '../../../components/CustomFileInput';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import generateEcgPDF from './generateEcgPDF';
import { getPrefix } from '../../../util/utils';
import {
  normalizeWhatsappPhone,
  enviarArquivo,
} from '../../../services/pdfWhatsapp';

const updateEcg = async ecg => {
  const { data } = await api.put(`/ecg/${ecg.codEcg}`, ecg);
  return data;
};

export default function FormEcg() {
  const dispatch = useDispatch();
  const { id: codLaudo } = useParams();
  const handleGoBack = () => {
    history.goBack();
  };
  const { data, error, isLoading, mutate } = useEcg(codLaudo);
  const ecg = useMemo(() => data ?? [], [data]);
  const medico = useSelector(state => state.doctor.profile);
  const { funcionario } = useSelector(state => state.funcionario);
  const { show } = useContextMenu();
  const [fileToUpload, setFileToUpload] = useState([]);
  const [fileToView, setFileToView] = useState([]);
  const [url, setURL] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [loadingUploadImage, setLoadingUploadImage] = useState(false);
  const [loadingShowImage, setLoadingShowImage] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [isUpdatedInMenu, setIsUpdatedInMenu] = useState(false);
  const [currentImageBase64, setCurrentImageBase64] = useState(null);
  const laudo = ecg[0];
  const [descricao, setDescricao] = useState(() => laudo?.OBSERVACAO || '');
  const contentFooter = useMemo(
    () =>
      `<div align="right">
      <strong>Médico: ${getPrefix(laudo?.CODREALIZOU)} ${laudo?.Realizou
      }</strong><br>
      CRM: ${laudo?.CRMMED} - RQE: ${laudo?.RQE}<br>
      <i>Autenticação: ${md5(JSON.stringify(laudo || ''))}</i>
    </div>`,
    [laudo]
  );

  const fetchFiles = useCallback(async () => {
    try {
      const { data } = await api.get('/files', {
        params: {
          codLaudo: laudo?.CODECG,
          procedimento: 'ecg',
        },
      });

      const file = data.filter(
        item =>
          item.Key.endsWith('.jpg') ||
          item.Key.endsWith('.bmp') ||
          item.Key.endsWith('.BMP')
      );

      setFileToView(file);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao carregar arquivos!');
    }
  }, [laudo]);

  useEffect(() => {
    setURL(null);
    setDescricao(laudo?.OBSERVACAO || '');
  }, [laudo]);

  useEffect(() => {
    if (laudo?.CODECG) {
      fetchFiles();
    }
  }, [laudo, fetchFiles]);

  useEffect(() => {
    if (fileToView.length > 0) showFiles();
  }, [fileToView]);

  async function showFiles() {
    try {
      setLoadingShowImage(true);
      const { data } = await requestLaudoPresignDownload({
        key: fileToView[0].Key,
      });

      setURL(data?.preSignedUrl);

      // Convert the image to base64 for PDF generation
      const response = await fetch(data.preSignedUrl);
      if (!response.ok) {
        throw new Error('Imagem ainda não disponível.');
      }
      const blob = await response.blob();
      const base64 = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      setCurrentImageBase64(base64);
    } catch (error) {
      // TODO: Sentry aqui também!
      // Sentry.captureException(error);
      toast.error('Erro ao mostrar imagem.');
    } finally {
      setLoadingShowImage(false);
    }
  }

  async function handleUploadFile(file) {
    if (!file.length) return toast.warn('Selecione um arquivo!');

    const isInvalid = !['.jpg', '.bmp', '.BMP'].some(ext =>
      file[0].name.endsWith(ext)
    );
    if (isInvalid) return toast.warn('Selecione apenas arquivos .jpg ou .bmp.');

    try {
      setLoadingUploadImage(true);
      await uploadBlobToLaudo({
        procedimento: 'ecg',
        codLaudo: laudo?.CODECG,
        fileName: file[0].name,
        body: file[0],
      });

      await fetchFiles();
      toast.success('Arquivo enviado com sucesso!');
      setShowUpload(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingUploadImage(false);
    }
  }

  const handleMenu = (event, id) => {
    event.preventDefault();
    show(event, { id });
  };

  const handleMenuClick = ({ event }) => {
    // React 16 anula o evento sintético depois do handler. Ler o id
    // dentro do updater do setState quebra com currentTarget null.
    const frase = event.currentTarget.id;
    setIsUpdatedInMenu(true);
    setDescricao(atual => `${atual ?? ''}  ${frase}`);
    event.stopPropagation();
  };

  const handleGravar = useCallback(
    async richTextData => {
      try {
        const data = {
          codEcg: laudo?.CODECG,
          descricao: richTextData,
        };

        const response = await updateEcg(data);

        await mutate([{ ...laudo, OBSERVACAO: richTextData }], {
          revalidate: false,
        });
        toast.success(response.message);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Erro ao atualizar!');
      }
    },
    [laudo, mutate]
  );

  async function deleteFile(e, fileKey) {
    if (!fileKey) return;

    try {
      e.preventDefault();
      const res = await api.delete('/files', { data: { key: fileKey } });
      toast.success(res.data.message);
      await fetchFiles();
      setURL(null);
      setDeleteAlert(false);
    } catch (error) {
      toast.error(error.response.data.error);
    }
  }

  // TODO: melhorar isso. deve ter uma maneira melhor de buscar o prontuário a partir do ecg.
  async function fetchProntuario(atividade) {
    try {
      const res = await api.get(`pacientes/${atividade.CODPAC}/atividades`);
      const laudos = res.data.filter(
        l => l.CODPROCEDI === 1 || l.CODPROCEDI === 58 || l.CODPROCEDI === 800
      );

      if (!laudos.length) return toast.warn('Paciente não tem consulta!');

      await dispatch(selectProntuarioById(laudos[0]?.CODLAUDO));
      history.push('/formProntuario');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao buscar prontuário!');
    }
  }

  const handleAttachment = useCallback(() => {
    setShowUpload(true);
  }, []);

  // VELHO. APAGAR DEPOIS.
  async function handleEnviarEcg() {
    const celularWhats = normalizeWhatsappPhone(laudo?.CELULARPAC);
    if (!celularWhats) {
      toast.warn('Número de celular não encontrado!');
      return;
    }

    try {
      const pdfDocGenerator = await generateEcgPDF(laudo, currentImageBase64);

      if (!pdfDocGenerator) return;

      const data = await enviarArquivo({
        pdfGenerator: pdfDocGenerator,
        to: celularWhats,
        paciente: {
          id: laudo?.CODPAC,
          nome: laudo?.NOMEPAC,
          medico: laudo?.REALIZOU,
        },
        codLaudo: laudo?.CODECG,
        fileName: `Laudo_ECG_${laudo?.CODECG}.pdf`,
        caption: `Olá *${laudo?.NOMEPAC}*, tudo bem? 😊\n\nSegue em anexo o seu *Laudo de Eletrocardiograma*.\n\n
Qualquer dúvida, nossa equipe estará à disposição no setor de atendimento pelo WhatsApp 14-3402-5252\n
ICM - Instituto do Coração de Marília`,
        officialConfig: {
          procedimento: 'ecg',
          documentTitle: 'Laudo de Eletrocardiograma',
        },
      });
      toast.success(data.message);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        'Erro ao enviar laudo via WhatsApp';
      toast.error(errorMessage);
    }
  }

  if (isLoading || error || !ecg.length) {
    return (
      <Card className="mt-2">
        <LaudoCardHeader title="ELETROCARDIOGRAMA" />

        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '80vh',
            }}
          >
            <Spinner animation="border" variant="dark" size="lg" />
          </div>
        )}

        {!isLoading && (
          <>
            <span
              className="text-danger text-center d-block"
              style={{ marginTop: 10 }}
            >
              {error
                ? 'Erro ao carregar laudo de eletrocardiograma! Tente novamente.'
                : 'Laudo não encontrado.'}
            </span>
            <Card.Footer className="text-muted text-center">
              <Button variant="warning" onClick={handleGoBack}>
                Voltar
              </Button>
            </Card.Footer>
          </>
        )}
      </Card>
    );
  }

  return (
    <>
      <Card className="mt-2">
        <LaudoCardHeader title="ELETROCARDIOGRAMA" />
        <Card.Body>
          <strong className="mr-auto">Nome do Paciente: </strong>
          {`${laudo.CODPAC} - ${laudo.NOMEPAC}`}
          <br></br>
          <strong className="mr-auto">Data de Nasc: </strong>
          {`${laudo.dataNasc}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Sexo: </strong>
          {`${laudo.SEXOPAC}`}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Idade: </strong>
          {`${laudo.idadePAC} anos`}
          <br></br>
          <strong className="mr-auto">Convênio: </strong>
          {laudo.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Cod ECG: </strong>
          {laudo.CODECG}&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Data do Exame: </strong>
          {laudo.dataEcg}
          <br></br>
          <strong className="mr-auto">Médico que Realizou: Dr. </strong>
          {`${laudo.Realizou}`}
          <br></br>
          <strong className="mr-auto">Médico que Solicitou: Dr. </strong>
          {`${laudo.Solicitou}`}
          <Alert
            show={deleteAlert}
            variant="danger"
            onClose={() => setDeleteAlert(false)}
            dismissible
          >
            <Alert.Heading>
              Tem certeza que quer deletar a imagem?
            </Alert.Heading>
            <Button
              onClick={e => deleteFile(e, fileToView[0]?.Key)}
              variant="danger"
            >
              Confirmar
            </Button>
          </Alert>
          <div style={{ alignItems: 'center', textAlign: 'center' }}>
            {loadingShowImage ? (
              <Spinner animation="border" />
            ) : (
              <>
                {(medico ||
                  funcionario?.permissao === 'PARCIAL1' ||
                  funcionario?.permissao === 'TOTAL') &&
                  fileToView?.length > 0 && (
                    <button
                      style={{ marginRight: 5, color: 'red' }}
                      onClick={() => setDeleteAlert(true)}
                    >
                      Deletar imagem
                    </button>
                  )}
                {url ? (
                  <img
                    alt="Laudo ECG"
                    loading="lazy"
                    src={url}
                    width="100%"
                    height="80%"
                  />
                ) : (
                  <div className="text-center mt-2">
                    <i className="text-muted">Não há imagem para exibir.</i>
                  </div>
                )}
              </>
            )}
          </div>
          <Form.Group size="sm">
            <Form.Label className="d-flex justify-content-between">
              <strong>DESCRIÇÃO</strong>
              <OverlayTrigger overlay={<Tooltip>Descrição</Tooltip>}>
                <Button size="sm" onClick={e => handleMenu(e, 'idEcg')}>
                  <FaBars style={{ width: 18, height: 18 }} />
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Menu className={styles.styleMenu} id={'idEcg'}>
              {dbecg.map(e => (
                <Item
                  key={e.value}
                  id={e.value}
                  onClick={e => handleMenuClick(e)}
                  title={e.value} // Tooltip ao passar o mouse
                >
                  {e.value.length > 60
                    ? e.value.slice(0, 60).trim() + '...'
                    : e.value}
                </Item>
              ))}
            </Menu>
          </Form.Group>
          <Card.Footer className="mt-3 text-center">
            {funcionario && (
              <Button variant="info" onClick={handleGoBack}>
                Agenda
              </Button>
            )}{' '}
            {medico && (
              <>
                <Button variant="info" onClick={() => fetchProntuario(laudo)}>
                  Prontuário
                </Button>{' '}
                <Button variant="outline-success" onClick={handleEnviarEcg}>
                  <FaWhatsapp /> Enviar PDF
                </Button>{' '}
              </>
            )}{' '}
            {!funcionario && (
              <Button variant="danger" onClick={handleGoBack}>
                Voltar
              </Button>
            )}
          </Card.Footer>
          <RichTextEditor
            content={descricao}
            contentFooter={contentFooter}
            saveContent={handleGravar}
            handleAttachment={handleAttachment}
            imageUrl={url}
            appendContent={isUpdatedInMenu}
            readOnly={false}
          />
        </Card.Body>
      </Card>

      <Modal
        show={showUpload}
        centered
        onHide={() => {
          setShowUpload(false);
          setFileToUpload([]);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={false}
            onChange={e => setFileToUpload(Array.from(e.target.files))}
          />
          <ul>
            {fileToUpload?.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <li style={{ marginRight: 5 }}>{fileToUpload[0]?.name}</li>
                <FaTrashAlt
                  onClick={() => setFileToUpload([])}
                  cursor="pointer"
                  style={{ color: 'red' }}
                />
              </div>
            )}
          </ul>
          {fileToView.length > 0 && (
            <Alert variant="warning">
              <Alert.Heading>Eletro já tem uma imagem!</Alert.Heading>
              <p>Delete a imagem atual para anexar outra.</p>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {loadingUploadImage ? (
            <Spinner animation="border" variant="dark" />
          ) : (
            <Button
              onClick={() => handleUploadFile(fileToUpload)}
              disabled={fileToView.length > 0}
            >
              Confirmar
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
